import React from 'react';
import * as yup from 'yup';
import { Dialog } from 'primereact/dialog';
import { DayEnum } from '@api/ringil-api.v2';
import { isNotNullOrUndefined } from '@utils/utils';
import useForm from '@hooks/useForm/useForm';
import useTranslation from '@hooks/i18n/useTranslation';
import { minutesToTimeString } from '@utils/dateWithTz';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import { FieldLabel } from '@components/framework/formfield/FieldLabel';
import InputHour from '@components/obsolete/Form/InputHour/InputHour';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect';
import COSelect from '@components/ringil3/COSelect/COSelect';

interface ComponentProps {
  generateForDay: DayEnum | null;
  onGenerateSlots: (slots: GeneratedSlot[]) => void;
  onHide: () => void;
}

interface Form {
  from: string;
  to: string;
  slotDuration: number;
}

export interface GeneratedSlot {
  sinceHourString: string;
  tillHourString: string;
}

const DURATIONS = [15, 30, 60];

const hourStringToMinutes = (hourString: string): number => {
  const [h, m] = (hourString ?? '').split(':');
  const hour = parseInt(h);
  const minutes = parseInt(m);
  if (isNaN(hour) || isNaN(minutes)) return NaN;
  return hour * 60 + minutes;
};

const buildSlots = (from: string, to: string, slotDuration: number): GeneratedSlot[] => {
  const start = hourStringToMinutes(from);
  const end = hourStringToMinutes(to);
  if (isNaN(start) || isNaN(end) || slotDuration <= 0) return [];
  const slots: GeneratedSlot[] = [];
  for (let current = start; current + slotDuration <= end; current += slotDuration) {
    slots.push({ sinceHourString: minutesToTimeString(current), tillHourString: minutesToTimeString(current + slotDuration) });
  }
  return slots;
};

const GenerateDialog: React.FC<ComponentProps> = ({ generateForDay, onHide, onGenerateSlots }) => {
  const { tr } = useTranslation();

  const form = useForm<Form>(
    yup.object(),
    { from: '08:00', to: '20:00', slotDuration: 30 },
    values => onGenerateSlots(buildSlots(values.from, values.to, values.slotDuration)),
    false,
    false,
  );

  const slots = buildSlots(form.values.from, form.values.to, form.values.slotDuration);

  return (
    // @ts-ignore PrimeReact Dialog children typing mismatch (same as ConfirmDialog)
    <Dialog
      visible={isNotNullOrUndefined(generateForDay)}
      baseZIndex={1000000}
      onHide={onHide}
      header={<h4>{tr('GenereateDialog.generateOpeningHours', 'Generate opening hours')}</h4>}
      style={{ width: '520px', maxWidth: '95vw' }}>
      <div className="flex flex-col gap-4">
        {generateForDay && <span className="text-base font-medium text-content-primary">{tr(`TimeIntervalComponent.days${generateForDay}`, generateForDay)}</span>}

        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <FieldLabel label={tr('GenereateDialog.generateFrom', 'Generate from')} />
            <div className="w-[92px] [&_input]:w-full">
              <InputHour value={form.values.from} onChange={v => form.setFieldValue(form.names.from, v)} dataTestId="location-openingHours-generate-from" />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <FieldLabel label={tr('GenereateDialog.generateTo', 'Generate to')} />
            <div className="w-[92px] [&_input]:w-full">
              <InputHour value={form.values.to} onChange={v => form.setFieldValue(form.names.to, v)} dataTestId="location-openingHours-generate-to" />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <FieldLabel label={tr('GenereateDialog.slotDurationMin', 'Slot duration [min]')} />
            <InputSelect
              model={DURATIONS.map(d => ({ code: String(d), label: String(d) }))}
              selectedItemCode={String(form.values.slotDuration)}
              onSelect={code => form.setFieldValue(form.names.slotDuration, Number(code))}
              dataTestIdPrefix="location-openingHours-generate-duration"
            />
          </div>
        </div>

        {/* Fixed height + scroll so the dialog never resizes as the slot count changes with duration/range.
            Equal-column grid + tabular-nums (inherited into COSelect) = uniform, column-aligned chips regardless of digit widths. */}
        <div className="grid h-[200px] grid-cols-3 content-start justify-items-center gap-2 overflow-y-auto tabular-nums">
          {slots.map((s, i) => (
            <COSelect key={i} text={`${s.sinceHourString} - ${s.tillHourString}`} dataTestId={`location-openingHours-generate-slot-${i}`} />
          ))}
        </div>

        <div className="flex flex-row gap-4">
          <RingilButton
            mode="filled-rectangle"
            label={tr('LocationOpeningHours.generate', 'Generate')}
            onClick={() => form.validateAndSend()}
            disabled={slots.length === 0}
            dataTestId="location-openingHours-generate-submit"
          />
          <RingilButton mode="text-only" label={tr('generic.cancel', 'Cancel')} onClick={onHide} dataTestId="location-openingHours-generate-cancel" />
        </div>
      </div>
    </Dialog>
  );
};

export default GenerateDialog;
